/**
 * NOLAS 2026 (Mikaël FOURNY).
 * src/i18n/fr.inscriptionCode.ts
 *
 * Clés de l'écran « saisie du code d'accès » (ExhibitorSignupPage, phase
 * code), servi sur /exposant/inscription ET /animateur/inscription :
 *   - les libellés propres à l'adresse animateur (titre, sous-titre, champ),
 *     que l'écran affichait jusqu'ici en version exposant ;
 *   - l'indicateur d'étapes discret sous le sous-titre ;
 *   - la bulle « Comment ça marche ? ».
 *
 * Chaque phrase de la bulle a été confrontée au code, le 2026-10-01 :
 *   - demande d'accès : exhibitor/request-code.php, file d'attente admin ;
 *   - validation par l'organisation puis envoi du code par e-mail :
 *     admin/exhibitor-code-requests.php (approve → send_expo_code_email) ;
 *   - préfixe : « EXPO » + 6 caractères pour les DEUX profils (un seul espace
 *     de codes, `profile_kind` distingue l'animateur) ;
 *   - code à usage unique : consommé par register/with-code.php ;
 *   - étapes du wizard : STEP_KEYS (6) et HOST_STEP_KEYS (3) de la page ;
 *   - badge créé à la fin de l'inscription : visitor_ensure_qr_payload dans
 *     register/with-code.php, AUCUN e-mail envoyé à ce moment-là ;
 *   - connexion : /connexion, adresse e-mail puis code à 6 chiffres reçu par
 *     e-mail (visitor-magic-code.php), sans mot de passe.
 *
 * Dans la bulle, « e‑mail » porte un trait d'union INSÉCABLE (U+2011) : à
 *   375 px, le mot se coupait en « e- / mail ».
 *
 * ⚠ Jamais « atelier » en dur : jetons {lexWorkshop*}. Jamais de tiret
 *   cadratin. Apostrophe typographique (’) uniquement.
 *
 * Mikael - chef de projet - 2026-10-01
 */
export const messagesInscriptionCodeFr: Record<string, string> = {
  // ── Libellés propres à l'adresse /animateur/inscription ───────────────
  // Avant ce lot, l'adresse animateur affichait « Inscription exposant » et
  // « Code d'accès exposant » : la route présélectionne le profil, l'écran
  // doit le dire. Mikael - chef de projet - 2026-10-01
  'signupCode.host.title': 'Inscription animateur',
  'signupCode.host.subtitle':
    'Saisissez le code « EXPO » communiqué par l’organisation pour démarrer votre inscription d’animateur.',
  'signupCode.host.label': 'Code d’accès animateur',
  'signupCode.host.closed': 'Les inscriptions animateurs sont actuellement fermées.',

  // ── Indicateur d'étapes (trois repères, l'étape courante est la 1) ────
  'signupCode.steps.label': 'Les étapes de votre inscription',
  'signupCode.steps.code': 'Code d’accès',
  'signupCode.steps.exhibitorForm': 'Votre fiche',
  'signupCode.steps.hostForm': 'Vos {lexWorkshopPlural}',
  'signupCode.steps.login': 'Connexion et badge',

  // ── Bulle « Comment ça marche ? » ─────────────────────────────────────
  'signupCode.help.toggle': 'Comment ça marche ?',
  'signupCode.help.close': 'Fermer',
  'signupCode.help.intro':
    'Le code d’accès commence par EXPO, suivi de 6 caractères. L’organisation vous l’envoie par e‑mail. Il ne sert qu’une fois, pour créer votre compte.',
  'signupCode.help.stepsTitle': 'Les étapes',
  'signupCode.help.step.request': 'Vous demandez un accès.',
  'signupCode.help.step.validate': 'L’organisation valide votre demande.',
  'signupCode.help.step.email': 'Vous recevez votre code par e‑mail.',
  'signupCode.help.here': 'Vous êtes ici :',
  'signupCode.help.step.signupExhibitor':
    'saisissez le code, puis complétez votre fiche en 6 étapes (identité, entreprise, offre, postes, stand, visibilité).',
  'signupCode.help.step.signupHost':
    'saisissez le code, puis complétez votre inscription en 3 étapes (identité, vos {lexWorkshopPlural}, récapitulatif).',
  'signupCode.help.step.badge': 'Votre badge est créé à la fin de l’inscription.',
  'signupCode.help.step.login':
    'Vous vous connectez avec votre adresse e‑mail : un code à 6 chiffres vous est envoyé par e‑mail, sans mot de passe. Votre badge vous attend dans votre espace.',
  'signupCode.help.noCode': 'Pas encore de code ? Commencez par « demander un accès ».',
  'signupCode.help.already': 'Déjà un compte ou un badge ? Passez par « me connecter ».',
  // Lien affiché seulement si `help.tutorial_exhibitor_url` ou
  // `help.tutorial_host_url` porte une adresse http(s). Mikael - chef de projet - 2026-10-01
  'signupCode.help.tutorial': 'Voir le tutoriel pas à pas',
  'signupCode.help.newTab': '(s’ouvre dans un nouvel onglet)',
};
